import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronRight, Clock, BarChart3, Home } from 'lucide-react';
import { useAuth, getApiBase } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import TopicSelector from './TopicSelector';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import '../styles/AdaptiveQuiz.css';

// Math formatter: convert LaTeX $...$ to HTML with KaTeX
// Shared between AdaptiveQuiz and QuizResults components
const formatMath = (text) => {
  if (!text) return '';
  
  let html = String(text);
  
  // First, handle escaped dollar signs \$ and convert to regular $
  html = html.replace(/\\\$/g, '$');
  
  // Replace $...$ patterns with KaTeX-rendered HTML
  html = html.replace(/\$([^$]+)\$/g, (match, latex) => {
    try {
      return katex.renderToString(latex, { throwOnError: false });
    } catch (e) {
      console.warn('KaTeX render error:', e);
      return match;
    }
  });
  
  // Also handle ^digits for fallback (convert to <sup>)
  html = html.replace(/\^(\d+)/g, '<sup>$1</sup>');
  
  return html;
};

export default function AdaptiveQuiz({ userId, onComplete }) {
  const { user: authUser } = useAuth();
  const { language } = useLanguage();
  const finalUserId = userId || authUser?.id;
  const location = useLocation();
  const navigate = useNavigate();
  
  const [quiz, setQuiz] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [results, setResults] = useState(null);
  const [timeStarted, setTimeStarted] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [showTopicSelector, setShowTopicSelector] = useState(false);

  useEffect(() => {
    // Check if quiz data was passed via navigation state
    if (location.state?.quiz) {
      console.log('[AdaptiveQuiz] ✅ Using quiz from location state');
      const quizData = Array.isArray(location.state.quiz) ? location.state.quiz : location.state.quiz.questions || [];
      console.log('[AdaptiveQuiz] Quiz data received:', {
        isArray: Array.isArray(quizData),
        length: quizData.length,
        firstQuestion: quizData.length > 0 ? {
          id: quizData[0].id,
          hasQuestion: !!quizData[0].question,
          hasOptions: Array.isArray(quizData[0].options),
          typeFromBackend: quizData[0].type,
          cognitiveLevel: quizData[0].cognitiveLevel
        } : null
      });
      
      if (quizData.length > 0) {
        const questionsWithIds = quizData.map((q, idx) => {
          // Use type from backend if available, otherwise detect from structure
          let qType = q.type || 'multiple-choice';
          if (!q.type) {
            // Fallback: detect question type based on structure
            if (q.statements && Array.isArray(q.statements)) {
              qType = 'true-false';
            } else if (q.numerical_answer !== undefined || q.text_answer !== undefined) {
              qType = 'short-answer';
            }
          }
          
          return {
            ...q,
            id: q.id !== undefined && q.id !== null ? q.id : `q-${idx}`,
            type: qType,
            cognitiveLevel: q.cognitiveLevel || q.difficulty || 1  // Ensure cognitiveLevel is set
          };
        });

        const newQuiz = {
          questions: questionsWithIds,
          userId: finalUserId,
          questionCount: questionsWithIds.length,
          recommendation: location.state.recommendation,
          quizType: location.state.quizType,
          topic: location.state.topic || location.state.topicName || location.state.quiz?.topic || 'general'  // Get topic from multiple places
        };
        console.log('[AdaptiveQuiz] ✅ Quiz loaded successfully with', questionsWithIds.length, 'questions');
        console.log('[AdaptiveQuiz] Sample questions:', questionsWithIds.slice(0, 2).map(q => ({
          id: q.id,
          type: q.type,
          cognitiveLevel: q.cognitiveLevel,
          hasQuestion: !!q.question,
          hasOptions: Array.isArray(q.options) ? q.options.length : 0
        })));
        setQuiz(newQuiz);
        setShowTopicSelector(false);  // 🎯 Hide TopicSelector and show quiz
        setTimeStarted(Date.now());
        setLoading(false);
      } else {
        console.error('[AdaptiveQuiz] ❌ Quiz data is empty');
        setShowTopicSelector(true);  // Show TopicSelector if quiz is empty
        setLoading(false);
      }
    } else if (finalUserId) {
      // No location state - show TopicSelector for user to pick a topic
      console.log('[AdaptiveQuiz] No location state, showing TopicSelector');
      setShowTopicSelector(true);
      setLoading(false);
    } else {
      console.warn('[AdaptiveQuiz] ⚠️ No userId and no quiz data');
      setLoading(false);
    }
  }, [finalUserId, location.state]);

  // Log state changes for debugging
  useEffect(() => {
    console.log('[AdaptiveQuiz] State changed:', {
      showTopicSelector,
      hasQuiz: !!quiz,
      questionsCount: quiz?.questions?.length || 0,
      loading,
      results: !!results
    });
  }, [showTopicSelector, quiz, loading, results]);

  // Timer effect
  useEffect(() => {
    if (!timeStarted || results) return;

    const interval = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - timeStarted) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [timeStarted, results]);

  const loadPersonalizedQuiz = async () => {
    try {
      setLoading(true);
      const apiBase = getApiBase();
      const response = await fetch(`${apiBase}/api/adaptive/quiz/personalized?userId=${finalUserId}`);
      
      if (!response.ok) throw new Error('Failed to load quiz');
      
      const data = await response.json();
      // Backend returns {quiz: [...], questionCount, userId, message}
      const questions = data.quiz || data.questions || data;
      
      // Ensure each question has a unique ID and type field (use index as fallback)
      const questionsWithIds = Array.isArray(questions) ? questions.map((q, idx) => {
        // Detect question type based on structure (matching QuizPage logic)
        let qType = 'multiple-choice';
        if (q.statements && Array.isArray(q.statements)) {
          qType = 'true-false';
        } else if (q.numerical_answer !== undefined || q.text_answer !== undefined) {
          qType = 'short-answer';
        }
        
        return {
          ...q,
          id: q.id !== undefined && q.id !== null ? q.id : `q-${idx}`,
          type: qType
        };
      }) : [];
      
      console.log('[AdaptiveQuiz] Loaded questions:', questionsWithIds.length, 'Types:', questionsWithIds.map(q => ({ id: q.id, type: q.type })));
      
      setQuiz({
        questions: questionsWithIds,
        userId: finalUserId,
        questionCount: questionsWithIds.length
      });
      setTimeStarted(Date.now());
    } catch (err) {
      console.error('Error loading quiz:', err);
      setQuiz({ questions: [], error: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (questionIndex, selectedAnswer) => {
    setAnswers(prev => {
      const updated = {
        ...prev,
        [questionIndex]: selectedAnswer
      };
      // Better logging for object answers (true/false)
      let answerDisplay = selectedAnswer;
      if (typeof selectedAnswer === 'object' && selectedAnswer !== null) {
        answerDisplay = JSON.stringify(selectedAnswer);
      }
      console.log('[AdaptiveQuiz] Answer selected - Index:', questionIndex, 'Answer:', answerDisplay, 'AllAnswers:', updated);
      return updated;
    });
  };

  const handleNext = () => {
    if (currentQuestion < quiz.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmitQuiz = async () => {
    try {
      setAnalyzing(true);

      // Prepare answers in expected format for backend
      // Convert from index-based answers to question ID format
      const formattedAnswers = quiz.questions.map((q, idx) => ({
        questionId: q.id,
        questionType: q.type || 'multiple-choice',  // Include question type
        answer: answers[idx] || null
      }));

      // Filter out any null answers (unanswered questions)
      const answeredQuestions = formattedAnswers.filter(a => a.answer !== null);

      console.log('[AdaptiveQuiz] Quiz submission details:', {
        totalQuestions: quiz.questions.length,
        answeredQuestions: answeredQuestions.length,
        unansweredQuestions: quiz.questions.length - answeredQuestions.length,
        allAnswers: formattedAnswers.map(a => ({ id: a.questionId, type: a.questionType, answered: a.answer !== null }))
      });

      // Require at least one answer before submitting
      if (answeredQuestions.length === 0) {
        alert('Please answer at least one question before submitting.');
        setAnalyzing(false);
        return;
      }

      const payload = {
        userId: finalUserId,  // Use finalUserId which is either from prop or auth context
        quizId: 'personalized',
        personalizedQuizData: quiz.questions,  // Send the actual questions for analysis
        answers: formattedAnswers, // Send all answers including nulls - backend will handle them
        timeSpent: elapsedTime
      };

      console.log('[AdaptiveQuiz] Sending payload:', {
        userId: payload.userId,
        quizId: payload.quizId,
        questionsCount: payload.personalizedQuizData?.length,
        answersCount: payload.answers.length,
        timeSpent: payload.timeSpent,
        firstAnswer: payload.answers[0],
        lastAnswer: payload.answers[payload.answers.length - 1]
      });

      const apiBase = getApiBase();
      const response = await fetch(`${apiBase}/api/adaptive/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const responseText = await response.text();
      console.log('[AdaptiveQuiz] Response status:', response.status);
      console.log('[AdaptiveQuiz] Response body:', responseText);

      if (!response.ok) {
        let errorData;
        try {
          errorData = JSON.parse(responseText);
        } catch {
          errorData = { error: responseText };
        }
        console.error('[AdaptiveQuiz] Backend error response:', errorData);
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}: Failed to analyze quiz`);
      }

      const analysisResults = JSON.parse(responseText);
      console.log('[AdaptiveQuiz] Analysis successful:', {
        overallScore: analysisResults.overallScore,
        correctAnswers: analysisResults.correctAnswers,
        aiCoachFeedback: analysisResults.aiCoachFeedback ? analysisResults.aiCoachFeedback.substring(0, 100) + '...' : 'N/A',
        aiSource: analysisResults.aiSource,
        cognitiveAnalysis: analysisResults.cognitiveAnalysis,
        topicFeedbackCount: Object.keys(analysisResults.topicFeedback || {}).length,
        hasBrainPath: !!analysisResults.learningPath,
        savedToDatabase: analysisResults.savedToDatabase
      });
      
      // Verify AI feedback was generated properly
      if (analysisResults.aiSource === 'fallback') {
        console.warn('[AdaptiveQuiz] ⚠️ Using fallback AI feedback - OpenAI API may not be available');
      } else if (analysisResults.aiSource === 'openai') {
        console.log('[AdaptiveQuiz] ✅ Using real OpenAI-generated feedback');
      }
      
      // NOW SAVE TO /api/results TO UPDATE SUPABASE WITH BLOOM LEVELS AND WEAK/STRONG AREAS
      // This ensures all data is synced including cognitive_levels
      try {
        console.log('[AdaptiveQuiz] Saving complete results to Supabase via /api/results...');
        
        // CRITICAL: Validate topic consistency BEFORE saving
        const questionsTopics = [...new Set(quiz.questions.map(q => q.topic || q.chapterName))];
        const selectedTopic = quiz.topic;
        
        // Improved topic matching: handle Bloom level prefixes
        // Questions have format: "Nhận biết (Knowledge) - Khái niệm Hàm số"
        // Selected topic: "Hàm số và đồ thị"
        // Extract chapter name from question topic (text after " - ")
        const extractChapterFromQuestionTopic = (questionTopic) => {
          if (!questionTopic) return '';
          const parts = questionTopic.split(' - ');
          // Return the last part (chapter name after Bloom level)
          return parts.length > 1 ? parts[parts.length - 1].toLowerCase().trim() : questionTopic.toLowerCase().trim();
        };
        
        // Improved matching: check if chapter name is contained in question topic OR exact match
        const matchingQuestions = quiz.questions.filter(q => {
          const qTopic = q.topic || q.chapterName || '';
          const qChapter = extractChapterFromQuestionTopic(qTopic);
          const selectedLower = selectedTopic.toLowerCase().trim();
          
          // Match if: exact match OR question topic contains selected topic OR extracted chapter matches
          return qTopic.toLowerCase().includes(selectedLower) || 
                 selectedLower.includes(qChapter) ||
                 qChapter.includes(selectedLower.replace(/\s+/g, ''));
        });
        
        const matchPercentage = (matchingQuestions.length / quiz.questions.length) * 100;
        
        console.log('[AdaptiveQuiz] 🔍 TOPIC VALIDATION:', {
          selectedTopic,
          questionsTopics,
          questionsCount: quiz.questions.length,
          matchingCount: matchingQuestions.length,
          matchPercentage: `${Math.round(matchPercentage)}%`,
          matchingLogic: 'Flexible matching handles Bloom level prefixes'
        });
        
        if (matchPercentage < 50) {
          // If less than 50% match, log warning
          console.warn('[AdaptiveQuiz] ⚠️ POTENTIAL TOPIC MISMATCH (may be data issue):', {
            selectedTopic,
            matchPercentage: Math.round(matchPercentage),
            matchingQuestions: matchingQuestions.length,
            totalQuestions: quiz.questions.length,
            topicsFound: questionsTopics,
            note: 'Saving under selected topic. If this persists, check master data'
          });
        } else {
          console.log('[AdaptiveQuiz] ✅ Topic validation passed:', matchPercentage.toFixed(0) + '% questions match selected topic');
        }
        
        const savePayload = {
          userId: finalUserId,
          quizId: 'personalized-adaptive',
          quizName: 'Adaptive Quiz',
          // 🔥 FIX: ALWAYS use selectedTopic (what user clicked on) for progress tracking
          // Don't use actual question topics - that breaks user progress tracking
          topic: selectedTopic || 'General',
          answers: formattedAnswers,
          questions: quiz.questions,
          score: Math.round(analysisResults.overallScore),  // Convert to 0-10 scale
          percentage: Math.round(analysisResults.overallScore * 10),  // Convert to 0-100 scale
          ai_analysis: analysisResults,
          timeTaken: elapsedTime,
          isAutoSubmitted: false,
          // Store actual question topics for debugging
          _actualQuestionTopics: questionsTopics
        };
        
        const saveRes = await fetch(`${apiBase}/api/results`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(savePayload)
        });
        
        console.log('[AdaptiveQuiz] 💾 SAVING TO /api/results with topic:', savePayload.topic);
        console.log('[AdaptiveQuiz] 📊 Save payload:', {
          userId: savePayload.userId,
          quizId: savePayload.quizId,
          topic: savePayload.topic,
          score: savePayload.score,
          percentage: savePayload.percentage,
          questionsCount: savePayload.questions?.length,
          answersCount: savePayload.answers?.length
        });
        
        if (saveRes.ok) {
          const savedResult = await saveRes.ok ? saveRes.json() : null;
          console.log('[AdaptiveQuiz] ✅ Successfully saved to Supabase with Bloom levels and weak/strong areas');
          console.log('[AdaptiveQuiz] Saved result:', savedResult);
          
          // Now fetch the updated profile to get latest Bloom levels
          console.log('[AdaptiveQuiz] Fetching updated profile with new Bloom levels...');
          try {
            const profileRes = await fetch(`${apiBase}/api/adaptive/profile/${finalUserId}`);
            if (profileRes.ok) {
              const updatedProfile = await profileRes.json();
              console.log('[AdaptiveQuiz] ✅ Fetched updated profile:', {
                scores: updatedProfile.scores,
                proficiency: updatedProfile.proficiency,
                weakAreas: updatedProfile.weakAreas?.length,
                strongAreas: updatedProfile.strongAreas?.length
              });
              
              // Store updated profile in session storage so LearningProfile can use it
              sessionStorage.setItem('updatedProfile', JSON.stringify(updatedProfile));
            } else {
              console.warn('[AdaptiveQuiz] ⚠️ Failed to fetch updated profile:', profileRes.status);
            }
          } catch (profileErr) {
            console.warn('[AdaptiveQuiz] ⚠️ Error fetching updated profile:', profileErr.message);
          }
        } else {
          console.warn('[AdaptiveQuiz] ⚠️ Failed to save to /api/results (non-blocking):', saveRes.status);
        }
      } catch (saveErr) {
        console.warn('[AdaptiveQuiz] ⚠️ Error saving to /api/results (non-blocking):', saveErr.message);
      }
      
      // Store results in session storage so other pages can access updated data
      sessionStorage.setItem('lastQuizResults', JSON.stringify(analysisResults));
      sessionStorage.setItem('profileRefreshNeeded', 'true');
      
      setResults(analysisResults);

      // Check if user scored >= 8.5 to show hard mode popup
      if (analysisResults.overallScore >= 8.5) {
        console.log('[AdaptiveQuiz] 🎯 Score >= 8.5, preparing hard mode popup');
        // Create a quiz object for the hard mode popup
        const adaptiveQuiz = {
          id: 'adaptive',
          title: language === 'vi' ? 'Bài Kiểm Tra Thích Hợp' : 'Adaptive Quiz',
          chapterId: 0, // Use 0 as marker for adaptive quiz
          type: 'adaptive'
        };
        // Navigate to QuizList with hard mode popup flag
        navigate('/quiz-list', {
          state: {
            showHardModePopup: true,
            quiz: adaptiveQuiz,
            fromAdaptiveQuiz: true
          }
        });
      }

      if (onComplete) {
        onComplete(analysisResults);
      }
    } catch (err) {
      console.error('[AdaptiveQuiz] Error submitting quiz:', err);
      alert('Error analyzing quiz: ' + err.message);
    } finally {
      setAnalyzing(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  // 🎯 Show TopicSelector if user hasn't picked a topic yet
  if (showTopicSelector) {
    console.log('[AdaptiveQuiz] 📋 Rendering TopicSelector component');
    return <TopicSelector />;
  }

  if (loading) {
    return (
      <div className="adaptive-quiz loading">
        <div className="loading-container">
          <div className="spinner"></div>
          <h2>Loading Your Personalized Quiz...</h2>
          <p>We're preparing questions tailored to your skill level</p>
        </div>
      </div>
    );
  }

  if (!quiz || !quiz.questions || quiz.questions.length === 0) {
    return (
      <div className="adaptive-quiz error">
        <div className="error-container">
          <h2>Unable to Load Quiz</h2>
          <p>{quiz?.error || 'Please try refreshing the page or contact support.'}</p>
          <button onClick={loadPersonalizedQuiz} className="btn-retry">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (results) {
    return <QuizResults results={results} timeSpent={elapsedTime} quizQuestions={quiz.questions} quizAnswers={answers} />;
  }

  const question = quiz?.questions?.[currentQuestion];
  
  // Guard: if no question is loaded, show loading state
  if (!question) {
    return (
      <div className="adaptive-quiz loading">
        <div className="loading-container">
          <div className="spinner"></div>
          <h2>Loading Question...</h2>
        </div>
      </div>
    );
  }
  
  const isAnswered = answers[currentQuestion] !== undefined && answers[currentQuestion] !== null;
  // Count only valid (non-null, non-undefined) answers using indices
  const answeredCount = Object.values(answers).filter(ans => ans !== null && ans !== undefined).length;
  const allAnswered = answeredCount === quiz.questions.length;
  // Progress based on answered questions, not current question
  const progressPercent = ((answeredCount) / quiz.questions.length) * 100;

  console.log('[AdaptiveQuiz] 🎯 Rendering quiz with', quiz.questions.length, 'questions, CurrentQ:', currentQuestion);

  return (
    <div className="adaptive-quiz">
      {/* Header */}
      <header className="quiz-header">
        <div className="quiz-title">
          <h1>Personalized Quiz</h1>
          <p>Questions adapted to your level</p>
        </div>

        <div className="quiz-stats">
          <div className="stat">
            <Clock size={18} />
            <span>{formatTime(elapsedTime)}</span>
          </div>
          <div className="stat">
            <span className="progress-badge">
              {currentQuestion + 1}/{quiz.questions.length}
            </span>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="progress-container">
        <div className="progress-bar">
          <motion.div
            className="progress-fill"
            initial={{ width: 0 }}
            animate={{ width: `${progressPercent}%` }}
            transition={{ duration: 0.5 }}
          ></motion.div>
        </div>
        <p className="progress-text">{progressPercent.toFixed(0)}% Complete</p>
      </div>

      {/* Main Content */}
      <div className="quiz-container">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestion}
            className="question-section"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {/* Cognitive Level Badge */}
            <div className="cognitive-badge">
              {getCognitiveLevelDisplay(question.cognitiveLevel)}
            </div>

            {/* Question */}
            <div className="question">
              <h2 dangerouslySetInnerHTML={{ __html: formatMath(question.question) }} />
              {question.description && (
                <p className="question-context" dangerouslySetInnerHTML={{ __html: formatMath(question.description) }} />
              )}
            </div>

            {/* Answer Options */}
            <div className="options">
              {question.type === 'true-false' ? (
                // True/False Question Type - Display statements with True/False buttons
                <div className="true-false-container">
                  <div className="statements-list">
                    {(question.statements || []).map((stmt, idx) => {
                      const statementContent = language === 'en' ? stmt.content_en : stmt.content_vn;
                      const userAnswers = answers[currentQuestion] || {};
                      const userAnswer = userAnswers[idx];
                      
                      return (
                        <div key={idx} className="statement-item">
                          <div
                            className="statement-text"
                            dangerouslySetInnerHTML={{ __html: formatMath(statementContent) }}
                          />
                          <div className="statement-buttons">
                            <motion.button
                              className={`tf-button true-btn ${
                                userAnswer === true ? 'selected' : ''
                              }`}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: idx * 0.05 }}
                              onClick={() => {
                                const updatedAnswers = { ...userAnswers, [idx]: true };
                                handleAnswerSelect(currentQuestion, updatedAnswers);
                              }}
                            >
                              ✓ {language === 'en' ? 'True' : 'Đúng'}
                            </motion.button>
                            <motion.button
                              className={`tf-button false-btn ${
                                userAnswer === false ? 'selected' : ''
                              }`}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: idx * 0.05 + 0.05 }}
                              onClick={() => {
                                const updatedAnswers = { ...userAnswers, [idx]: false };
                                handleAnswerSelect(currentQuestion, updatedAnswers);
                              }}
                            >
                              ✗ {language === 'en' ? 'False' : 'Sai'}
                            </motion.button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : question.type === 'short-answer' ? (
                // Short Answer Question Type
                <motion.div
                  className="short-answer-input"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0 }}
                >
                  <textarea
                    value={answers[currentQuestion] || ''}
                    onChange={(e) =>
                      handleAnswerSelect(currentQuestion, e.target.value)
                    }
                    placeholder={language === 'en' ? 'Enter your answer here...' : 'Nhập câu trả lời...'}
                    className="answer-textarea"
                    rows="4"
                  />
                  <p className="answer-hint">
                    {language === 'en' 
                      ? 'Write your answer clearly. Your response will be reviewed for accuracy.'
                      : 'Viết câu trả lời rõ ràng. Câu trả lời của bạn sẽ được kiểm tra lại'}
                  </p>
                </motion.div>
              ) : (
                // Multiple Choice (default)
                (question.options || []).map((option, idx) => (
                  <motion.label
                    key={idx}
                    className={`option ${
                      answers[currentQuestion] === idx ? 'selected' : ''
                    }`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                  >
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={idx}
                      checked={answers[currentQuestion] === idx}
                      onChange={(e) =>
                        handleAnswerSelect(currentQuestion, parseInt(e.target.value))
                      }
                    />
                    <span className="option-content" dangerouslySetInnerHTML={{ __html: formatMath(option) }} />
                  </motion.label>
                ))
              )}
            </div>

            {/* Question Info */}
            <div className="question-info">
              <p>
                <strong>Difficulty:</strong>{' '}
                {getDifficultyLabel(question.cognitiveLevel)}
              </p>
              {question.topic && (
                <p>
                  <strong>Topic:</strong> {question.topic}
                </p>
              )}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="navigation">
          <button
            onClick={handlePrevious}
            disabled={currentQuestion === 0}
            className="btn-nav btn-prev"
          >
            ← Previous
          </button>

          <div className="question-indicators">
            {quiz.questions.map((_, idx) => {
              const answerValue = answers[idx];
              const isQuestionAnswered = answerValue !== null && answerValue !== undefined;
              return (
              <motion.button
                key={idx}
                data-question-index={idx}
                data-is-answered={isQuestionAnswered}
                className={`indicator ${
                  idx === currentQuestion ? 'active' : ''
                } ${isQuestionAnswered ? 'answered' : ''}`}
                onClick={() => setCurrentQuestion(idx)}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                title={`Q${idx + 1} - ${isQuestionAnswered ? 'Answered' : 'Not answered'}`}
              >
                {idx + 1}
              </motion.button>
              );
            })}
          </div>

          {currentQuestion < quiz.questions.length - 1 ? (
            <button onClick={handleNext} className="btn-nav btn-next">
              Next →
            </button>
          ) : (
            <button
              onClick={handleSubmitQuiz}
              disabled={!allAnswered || analyzing}
              className={`btn-submit ${!allAnswered ? 'disabled' : ''}`}
            >
              {analyzing ? 'Analyzing...' : 'Submit Quiz'}
            </button>
          )}
        </div>

        {/* Answer Summary */}
        <div className="answer-summary">
          <p>Answered: {answeredCount} / {quiz.questions.length}</p>
          {!allAnswered && (
            <p className="warning">
              Answer all {quiz.questions.length - answeredCount} remaining questions before submitting
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Quiz Results Component
 * Displays adaptive analysis and personalized recommendations
 */
function QuizResults({ results, timeSpent, quizQuestions = [], quizAnswers = {} }) {
  const [viewingDetails, setViewingDetails] = useState(null);
  const [showReview, setShowReview] = useState(false);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  return (
    <div className="quiz-results">
      <header className="results-header">
        <h1>Quiz Complete!</h1>
        <p>Your Adaptive Analysis</p>
      </header>

      {/* Overall Performance */}
      <div className="results-container">
        <motion.section
          className="overall-score"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="score-circle">
            <div className="score-number-large">
              {Math.round(results.overallScore)}
            </div>
            <div className="score-divider-large">/</div>
            <div className="score-total-large">10</div>
          </div>
          <p className="time-info">
            Completed in: {formatTime(timeSpent)}
          </p>
        </motion.section>

        {/* AI Coach Feedback Section - PROMINENT */}
        {results.aiCoachFeedback && (
          <motion.section
            className="ai-coach-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            style={{
              background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
              borderLeft: '4px solid #0ea5e9'
            }}
          >
            <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              🤖 AI Coach Feedback
            </h2>
            <div style={{
              background: 'white',
              padding: '1.5rem',
              borderRadius: '8px',
              lineHeight: '1.8',
              fontSize: '1.05rem',
              color: '#1f2937'
            }}>
              <p>{results.aiCoachFeedback}</p>
              {results.aiSource === 'fallback' && (
                <p style={{ fontSize: '0.85rem', color: '#9ca3af', marginTop: '1rem' }}>
                  📌 Note: This is generated feedback. Check back after additional quizzes for personalized AI coaching.
                </p>
              )}
            </div>
          </motion.section>
        )}

        {/* Quiz Review Section */}
        {quizQuestions.length > 0 && (
          <motion.section
            className="quiz-review"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2>📋 Question Review</h2>
              <button
                onClick={() => setShowReview(!showReview)}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: showReview ? '#ef4444' : '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  transition: 'all 0.3s ease'
                }}
              >
                {showReview ? 'Hide' : 'Show'} Details
              </button>
            </div>

            {showReview && (
              <div className="review-grid" style={{ display: 'grid', gap: '1.5rem' }}>
                {quizQuestions.map((question, idx) => {
                  const userAnswer = quizAnswers[idx];
                  // For true/false questions, check if the answer object has any properties
                  const isTrueFalseQuestion = question.type === 'true-false' || (question.statements && Array.isArray(question.statements));
                  const isAnswered = userAnswer !== null && userAnswer !== undefined && 
                    (typeof userAnswer === 'object' && isTrueFalseQuestion ? Object.keys(userAnswer).length > 0 : true);
                  
                  // Determine correct answer based on question type
                  let isCorrect = false;
                  if (isAnswered) {
                    if (isTrueFalseQuestion && question.statements && Array.isArray(question.statements) && typeof userAnswer === 'object') {
                      // True/false: all statements must match
                      isCorrect = question.statements.every((stmt, i) => userAnswer[i] === stmt.is_true);
                    } else if (question.type === 'short-answer') {
                      // Short answer: text or numerical matching
                      if (question.text_answer !== undefined) {
                        isCorrect = String(userAnswer).toLowerCase().trim() === String(question.text_answer).toLowerCase().trim();
                      } else if (question.numerical_answer !== undefined) {
                        const userNum = parseFloat(userAnswer);
                        const correctNum = parseFloat(question.numerical_answer);
                        isCorrect = !isNaN(userNum) && Math.abs(userNum - correctNum) < 0.01;
                      }
                    } else if (question.type === 'multiple-choice') {
                      // Multiple choice: index matching
                      isCorrect = userAnswer === question.answerIndex;
                    }
                  }

                  return (
                    <motion.div
                      key={`review-${idx}`}
                      className="review-card"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 + idx * 0.05 }}
                      style={{
                        background: isAnswered ? (isCorrect ? '#d1fae5' : '#fee2e2') : '#f3f4f6',
                        border: `2px solid ${isAnswered ? (isCorrect ? '#10b981' : '#ef4444') : '#d1d5db'}`,
                        borderRadius: '8px',
                        padding: '1.5rem'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                        <div>
                          <h3 style={{ margin: '0 0 0.5rem 0' }}>
                            {isAnswered ? (isCorrect ? '✅' : '❌') : '⭕'} Question {idx + 1}
                          </h3>
                          <p style={{ margin: '0', color: '#6b7280', fontSize: '0.9rem' }}>
                            Type: <strong>{question.type || 'multiple-choice'}</strong>
                            {question.topic && ` | Topic: ${question.topic}`}
                          </p>
                        </div>
                      </div>

                      <div style={{ background: 'white', padding: '1rem', borderRadius: '6px', marginBottom: '1rem' }}>
                        <p style={{ margin: '0', fontWeight: '600', lineHeight: '1.6' }}>
                          {question.question || question.content_vn || question.english_question || 'Question text not available'}
                        </p>
                      </div>

                      <div style={{ display: 'grid', gap: '0.75rem' }}>
                        <div>
                          <p style={{ margin: '0', fontSize: '0.9rem', color: '#6b7280' }}>Your Answer:</p>
                          <p style={{ margin: '0.5rem 0 0 0', fontWeight: '600', color: isAnswered ? (isCorrect ? '#10b981' : '#ef4444') : '#6b7280' }}>
                            {isAnswered ? (
                              isTrueFalseQuestion && typeof userAnswer === 'object' && question.statements
                                ? <div style={{ marginTop: '0.5rem' }}>
                                    {question.statements.map((stmt, i) => (
                                      <div key={i} style={{ marginBottom: '0.5rem', padding: '0.5rem', background: '#f3f4f6', borderRadius: '4px' }}>
                                        <span>{stmt.content_vn || stmt.content_en || `Statement ${i + 1}`}</span>
                                        <strong style={{ marginLeft: '0.5rem' }}>
                                          {userAnswer[i] ? '✓ True' : '✗ False'}
                                        </strong>
                                      </div>
                                    ))}
                                  </div>
                                : question.type === 'short-answer'
                                ? `"${userAnswer}"`
                                : (question.options?.[userAnswer] || `Option ${userAnswer + 1}`)
                            ) : '(Not answered)'}
                          </p>
                        </div>

                        {isAnswered && !isCorrect && (isTrueFalseQuestion) && (
                          <div>
                            <p style={{ margin: '0', fontSize: '0.9rem', color: '#6b7280' }}>Correct Answer:</p>
                            {question.statements && Array.isArray(question.statements) ? (
                              <div style={{ marginTop: '0.5rem' }}>
                                {question.statements.map((stmt, i) => (
                                  <div key={i} style={{ marginBottom: '0.5rem', padding: '0.5rem', background: '#d1fae5', borderRadius: '4px' }}>
                                    <span>{stmt.content_vn || stmt.content_en || `Statement ${i + 1}`}</span>
                                    <strong style={{ marginLeft: '0.5rem' }}>
                                      {stmt.is_true ? '✓ True' : '✗ False'}
                                    </strong>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p style={{ margin: '0.5rem 0 0 0', fontWeight: '600', color: '#10b981' }}>
                                {question.correctAnswer ? '✓ True' : '✗ False'}
                              </p>
                            )}
                          </div>
                        )}

                        {isAnswered && !isCorrect && (question.type === 'short-answer') && (
                          <div>
                            <p style={{ margin: '0', fontSize: '0.9rem', color: '#6b7280' }}>Correct Answer:</p>
                            <p style={{ margin: '0.5rem 0 0 0', fontWeight: '600', color: '#10b981' }}>
                              {question.text_answer 
                                ? `"${question.text_answer}"` 
                                : (question.numerical_answer !== undefined 
                                  ? `${question.numerical_answer}` 
                                  : 'Not available')}
                            </p>
                          </div>
                        )}

                        {isAnswered && !isCorrect && (question.type === 'multiple-choice') && (
                          <div>
                            <p style={{ margin: '0', fontSize: '0.9rem', color: '#6b7280' }}>Correct Answer:</p>
                            <p style={{ margin: '0.5rem 0 0 0', fontWeight: '600', color: '#10b981' }}>
                              {question.options?.[question.correctAnswer] || (question.answerIndex !== undefined ? question.options?.[question.answerIndex] : 'Not available')}
                            </p>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </motion.section>
        )}

        {/* Cognitive Level Analysis */}
        <motion.section
          className="cognitive-analysis"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <h2>
            <BarChart3 size={20} />
            Cognitive Level Performance
          </h2>

          <div className="levels-grid">
            {renderCognitiveAnalysis(results.cognitiveAnalysis)}
          </div>
        </motion.section>

        {/* Topic Analysis */}
        {results.topicAnalysis && results.topicAnalysis.length > 0 && (
          <motion.section
            className="topic-analysis"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            <h2>📊 Performance by Topic</h2>
            <div className="topics-grid">
              {results.topicAnalysis.map((topic, idx) => (
                <motion.div
                  key={idx}
                  className={`topic-card topic-${topic.performance.toLowerCase()}`}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.15 + idx * 0.05 }}
                >
                  <h3>{topic.topic}</h3>
                  <div className="topic-score">
                    <span className="score">{topic.percentage}%</span>
                    <span className={`performance-badge ${topic.performance.toLowerCase()}`}>
                      {topic.performance}
                    </span>
                  </div>
                  <div className="topic-bar">
                    <div className="topic-fill" style={{ width: `${topic.percentage}%` }}></div>
                  </div>
                  <p className="topic-result">{topic.correct}/{topic.total} correct</p>
                  {topic.performance !== 'EXCELLENT' && topic.searchSuggestion && (
                    <p className="search-suggestion">
                      💡 Search for: <strong>{topic.searchSuggestion}</strong>
                    </p>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.section>
        )}

        {/* Topic-Level Detailed Feedback */}
        {results.topicFeedback && Object.keys(results.topicFeedback).length > 0 && (
          <motion.section
            className="topic-feedback"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h2>🎯 Topic-by-Topic Analysis</h2>
            <div className="feedback-cards">
              {Object.entries(results.topicFeedback).map(([topicName, feedback], idx) => (
                <motion.div
                  key={`feedback-${topicName}`}
                  className={`feedback-card topic-${feedback.performance.toLowerCase()}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 + idx * 0.08 }}
                  style={{
                    borderLeft: feedback.percentage >= 80 
                      ? '4px solid #10b981' 
                      : feedback.percentage >= 60 
                      ? '4px solid #f59e0b'
                      : '4px solid #ef4444'
                  }}
                >
                  <div className="feedback-header">
                    <h3>
                      {feedback.percentage >= 80 ? '✅' : feedback.percentage >= 60 ? '📚' : '⚠️'} {topicName}
                    </h3>
                    <span className="feedback-score" style={{
                      background: feedback.percentage >= 80 ? '#d1fae5' : feedback.percentage >= 60 ? '#fef3c7' : '#fee2e2',
                      color: feedback.percentage >= 80 ? '#065f46' : feedback.percentage >= 60 ? '#b45309' : '#7f1d1d',
                      padding: '0.5rem 1rem',
                      borderRadius: '20px',
                      fontWeight: '600'
                    }}>
                      {feedback.percentage}%
                    </span>
                  </div>
                  
                  {/* Main feedback message */}
                  <p style={{
                    fontSize: '1rem',
                    marginBottom: '1.5rem',
                    lineHeight: '1.6',
                    color: '#374151',
                    fontStyle: 'italic',
                    background: '#f9fafb',
                    padding: '1rem',
                    borderRadius: '6px'
                  }}>
                    💬 {feedback.feedback}
                  </p>
                  
                  {/* Quick Stats */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '1rem',
                    marginBottom: '1.5rem',
                    fontSize: '0.9rem'
                  }}>
                    <div style={{
                      background: '#f0f9ff',
                      padding: '0.75rem',
                      borderRadius: '6px',
                      textAlign: 'center'
                    }}>
                      <p style={{ color: '#0284c7', fontWeight: '600' }}>Correct Answers</p>
                      <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#0284c7' }}>{feedback.correct}/{feedback.total}</p>
                    </div>
                    <div style={{
                      background: '#fef3c7',
                      padding: '0.75rem',
                      borderRadius: '6px',
                      textAlign: 'center'
                    }}>
                      <p style={{ color: '#b45309', fontWeight: '600' }}>Accuracy Rate</p>
                      <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#b45309' }}>{feedback.percentage}%</p>
                    </div>
                  </div>
                  
                  {/* Improvement suggestions */}
                  {feedback.improvements && feedback.improvements.length > 0 && (
                    <div className="feedback-section">
                      <h4>🚀 How to Improve:</h4>
                      <ul style={{ listStyle: 'none', padding: '0', margin: '0.5rem 0 0 0' }}>
                        {feedback.improvements.map((improvement, i) => (
                          <li key={i} style={{
                            marginBottom: '0.5rem',
                            padding: '0.5rem',
                            background: '#f3f4f6',
                            borderRadius: '4px',
                            borderLeft: '3px solid #3b82f6'
                          }}>
                            • {improvement}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Learning Resources */}
                  {feedback.resources && feedback.resources.length > 0 && (
                    <div className="feedback-section">
                      <h4>📚 Learning Resources:</h4>
                      <ul style={{ listStyle: 'none', padding: '0', margin: '0.5rem 0 0 0' }}>
                        {feedback.resources.map((resource, i) => (
                          <li key={i} style={{
                            marginBottom: '0.5rem',
                            padding: '0.5rem',
                            background: '#f0fdf4',
                            borderRadius: '4px',
                            borderLeft: '3px solid #10b981'
                          }}>
                            🔗 {resource}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.section>
        )}

        {/* Answer Review Section */}
        {results.answerDetails && results.answerDetails.length > 0 && (
          <motion.section
            className="answer-review"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
          >
            <h2>📋 Review Your Answers</h2>
            <div className="answer-list">
              {results.answerDetails.map((answer, idx) => (
                <motion.div
                  key={`answer-${idx}`}
                  className={`answer-item ${answer.isCorrect ? 'correct' : 'incorrect'}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.25 + idx * 0.03 }}
                >
                  <div className="answer-header">
                    <div className="answer-status">
                      {answer.isCorrect ? <span className="badge-correct">✓ Correct</span> : <span className="badge-incorrect">✗ Incorrect</span>}
                    </div>
                    <div className="answer-metadata">
                      <span className="topic-tag">{answer.topic}</span>
                      <span className="difficulty-tag">Difficulty: {answer.difficulty === '1' ? 'Easy' : answer.difficulty === '2' ? 'Medium' : answer.difficulty === '3' ? 'Hard' : 'Expert'}</span>
                    </div>
                  </div>
                  
                  <div className="question-text">
                    <strong>Question {idx + 1}:</strong> <span dangerouslySetInnerHTML={{ __html: formatMath(answer.questionText) }} />
                  </div>
                  
                  <div className="answer-details">
                    <div className="student-answer">
                      <p>
                        <strong>Your Answer:</strong>{' '}
                        {answer.studentAnswer !== null && answer.studentAnswer !== undefined 
                          ? answer.statements && Array.isArray(answer.statements)
                            ? // For True/False questions - show the statement with answer
                              <span>
                                {Object.entries(answer.studentAnswer || {}).map(([idx, value]) => {
                                  const statementIdx = parseInt(idx, 10);
                                  const statement = answer.statements[statementIdx];
                                  return (
                                    <div key={idx} style={{ marginTop: '0.5rem', padding: '0.5rem', background: '#f3f4f6', borderRadius: '4px' }}>
                                      <span dangerouslySetInnerHTML={{ __html: formatMath((statement?.content_vn || statement?.content_en || '')) }} /> 
                                      <strong style={{ marginLeft: '0.5rem' }}>{value ? '✓ Đúng' : '✗ Sai'}</strong>
                                    </div>
                                  );
                                })}
                              </span>
                            : (answer.options && answer.options[answer.studentAnswer]) 
                              ? <span dangerouslySetInnerHTML={{ __html: formatMath(answer.options[answer.studentAnswer]) }} />
                              : <span dangerouslySetInnerHTML={{ __html: formatMath(String(answer.studentAnswer)) }} />
                          : 'Not answered'
                        }
                      </p>
                    </div>
                    {!answer.isCorrect && answer.correctAnswer !== null && answer.correctAnswer !== undefined && (
                      <div className="correct-answer">
                        <p>
                          <strong>Correct Answer:</strong>{' '}
                          {answer.statements && Array.isArray(answer.statements)
                            ? // For True/False - show correct answers
                              <span>
                                {answer.statements.map((stmt, idx) => (
                                  <div key={idx} style={{ marginTop: '0.5rem', padding: '0.5rem', background: '#d1fae5', borderRadius: '4px' }}>
                                    <span dangerouslySetInnerHTML={{ __html: formatMath((stmt?.content_vn || stmt?.content_en || '')) }} />
                                    <strong style={{ marginLeft: '0.5rem' }}>{stmt?.is_true ? '✓ Đúng' : '✗ Sai'}</strong>
                                  </div>
                                ))}
                              </span>
                            : (answer.options && answer.options[answer.correctAnswer])
                              ? <span dangerouslySetInnerHTML={{ __html: formatMath(answer.options[answer.correctAnswer]) }} />
                              : <span dangerouslySetInnerHTML={{ __html: formatMath(String(answer.correctAnswer)) }} />
                          }
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {answer.explanation && (
                    <div className="explanation">
                      <p><strong>💡 Explanation:</strong> {answer.explanation}</p>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.section>
        )}

        {/* Weak Areas */}
        {results.learningProfile?.weakAreas && results.learningProfile.weakAreas.length > 0 && (
          <motion.section
            className="weak-areas"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
          >
            <h2>⚠️ Areas Needing Improvement</h2>
            <div className="weak-areas-list">
              {results.learningProfile.weakAreas.map((area, idx) => {
                // Handle both object and string formats
                const topicName = typeof area === 'string' ? area : area.topWeakTopic || area.levelName || `Level ${area.level}`;
                const score = typeof area === 'object' ? (area.score || 0) : 0;
                return (
                  <div key={`weak-${idx}`} className="weak-area-item">
                    <span className="weak-area-topic">{topicName}</span>
                    {score !== undefined && score !== null && (
                      <span className="weak-area-score">{score}%</span>
                    )}
                  </div>
                );
              })}
            </div>
          </motion.section>
        )}

        {/* Strong Areas */}
        {results.learningProfile?.strongAreas && results.learningProfile.strongAreas.length > 0 && (
          <motion.section
            className="strong-areas"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.32 }}
          >
            <h2>✨ Your Strengths</h2>
            <div className="strong-areas-list">
              {results.learningProfile.strongAreas.map((area, idx) => {
                // Handle both object and string formats
                const topicName = typeof area === 'string' ? area : area.topicName || area.levelName || `Level ${area.level}`;
                const score = typeof area === 'object' ? (area.score || 0) : 0;
                return (
                  <div key={`strong-${idx}`} className="strong-area-item">
                    <span className="strong-area-topic">{topicName}</span>
                    {score !== undefined && score !== null && (
                      <span className="strong-area-score">{score}%</span>
                    )}
                  </div>
                );
              })}
            </div>
          </motion.section>
        )}

        {/* AI Coach Feedback */}
        {results.aiCoachFeedback && (
          <motion.section
            className="ai-feedback"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.36 }}
          >
            <h2>Phản hồi từ AI Coach</h2>
            <div className="feedback-content">
              <p>{results.aiCoachFeedback}</p>
              <small style={{color: '#666', marginTop: '10px', display: 'block'}}>
                {results.aiSource === 'openai' ? '(Được tạo bởi OpenAI)' : '(Được tạo tự động)'}
              </small>
            </div>
          </motion.section>
        )}

        {/* Learning Roadmap */}
        {(results.learningPath || results.learningProfile?.learningPath) && (
          <motion.section
            className="learning-roadmap"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
          >
            <h2>📚 Lộ trình học tập cá nhân hóa của bạn</h2>
            <div className="roadmap-content">
              {results.learningPath ? (
                <div className="roadmap-steps">
                  {Array.isArray(results.learningPath) ? (
                    results.learningPath.map((step, idx) => (
                      <div key={idx} className="roadmap-step">
                        <div className="step-number">{step.week || idx + 1}</div>
                        <div className="step-content">
                          <h4>{step.focus || step.title || `Tuần ${idx + 1}`}</h4>
                          {step.duration && <p className="duration">⏱️ {step.duration}</p>}
                          <p><strong>Hành động:</strong> {step.action || step.description || ''}</p>
                          <p><strong>Mục tiêu:</strong> {step.goal || ''}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p>{typeof results.learningPath === 'string' ? results.learningPath : 'Lộ trình học tập được tạo tự động'}</p>
                  )}
                </div>
              ) : results.learningProfile?.learningPath ? (
                <div className="roadmap-steps">
                  {typeof results.learningProfile.learningPath === 'string' ? (
                    <p>{results.learningProfile.learningPath}</p>
                  ) : Array.isArray(results.learningProfile.learningPath) ? (
                    results.learningProfile.learningPath.map((step, idx) => (
                      <div key={idx} className="roadmap-step">
                        <div className="step-number">{idx + 1}</div>
                        <div className="step-content">
                          <h4>{step.title || step.topic || `Bước ${idx + 1}`}</h4>
                          <p>{step.description || step.action || ''}</p>
                          {step.resources && (
                            <div className="step-resources">
                              <small>Tài nguyên: {step.resources}</small>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    Object.entries(results.learningProfile.learningPath).map(([key, value], idx) => (
                      <div key={idx} className="roadmap-step">
                        <div className="step-number">{idx + 1}</div>
                        <div className="step-content">
                          <h4>{key}</h4>
                          <p>{value}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              ) : null}
            </div>
          </motion.section>
        )}

        {/* Priority Actions from AI */}
        {results.aiSummary?.priority && results.aiSummary.priority.length > 0 && (
          <motion.section
            className="priority-actions"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.47 }}
          >
            <h2>🚀 Priority Actions (Do These First)</h2>
            <div className="priority-list">
              {results.aiSummary.priority.map((action, idx) => (
                <div key={`priority-${idx}`} className="priority-item">
                  <div className="priority-number">{idx + 1}</div>
                  <div className="priority-text">{action}</div>
                </div>
              ))}
            </div>
          </motion.section>
        )}

        {/* Strengths */}
        {results.strengths && results.strengths.length > 0 && (
          <motion.section
            className="strengths-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h2>🌟 Your Strengths</h2>
            <div className="strengths-list">
              {results.strengths.map((strength, idx) => (
                <div key={`strength-${idx}`} className="strength-item">
                  <p>{strength}</p>
                </div>
              ))}
            </div>
          </motion.section>
        )}

        {/* Areas to Improve */}
        {results.areasToImprove && results.areasToImprove.length > 0 && (
          <motion.section
            className="improve-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h2>📈 Areas to Improve</h2>
            <div className="improve-list">
              {results.areasToImprove.map((area, idx) => (
                <div key={`area-${idx}`} className="area-item">
                  <p>{area}</p>
                </div>
              ))}
            </div>
          </motion.section>
        )}

        {/* Next Steps */}
        {results.nextSteps && (
          <motion.section
            className="next-steps"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <h2>🎯 Your Next Steps</h2>
            <div className="next-steps-content">
              <p>{results.nextSteps}</p>
            </div>
          </motion.section>
        )}

        {/* Recommendations */}
        {results.recommendations && results.recommendations.length > 0 && (
          <motion.section
            className="recommendations"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <h2>💡 Personalized Recommendations</h2>
            <div className="recommendations-list">
              {results.recommendations.map((rec, idx) => (
                <motion.div
                  key={idx}
                  className="recommendation-item"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 + idx * 0.1 }}
                >
                  <h4>{rec.title}</h4>
                  <p>{rec.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.section>
        )}

        {/* Question Review Section */}
        {results.answerDetails && results.answerDetails.length > 0 && (
          <motion.section
            className="question-review"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <h2>Đánh giá chi tiết từng câu hỏi</h2>
            <div className="question-review-list">
              {results.answerDetails.map((detail, idx) => (
                <motion.div
                  key={idx}
                  className={`question-review-item ${detail.isCorrect ? 'correct' : 'incorrect'}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 + idx * 0.05 }}
                >
                  <div className="question-review-header">
                    <div className="question-number-badge">
                      {detail.isCorrect ? '✅' : '❌'} Câu {idx + 1}
                    </div>
                    <span className="topic-badge">{detail.topic}</span>
                    <span className="difficulty-badge">Độ khó: {detail.difficulty}</span>
                  </div>
                  
                  <div className="question-text">
                    <p><strong>Nội dung:</strong> {detail.questionText}</p>
                  </div>
                  
                  <div className="question-answers">
                    <div className="answer-section">
                      <p><strong>Câu trả lời của bạn:</strong> {
                        detail.questionType === 'true_false' || detail.statements
                          ? // For True/False questions
                            detail.statements ? detail.statements.map((stmt, idx) => (
                              <div key={idx} style={{ marginTop: '0.5rem', padding: '0.5rem', background: '#f3f4f6', borderRadius: '4px' }}>
                                <span>{stmt.content_vn || stmt.content_en || 'Statement'}</span>
                                <strong style={{ marginLeft: '0.5rem' }}>
                                  {detail.studentAnswer && detail.studentAnswer[idx] !== undefined 
                                    ? detail.studentAnswer[idx] ? '✓ Đúng' : '✗ Sai'
                                    : '⭕ Không trả lời'}
                                </strong>
                              </div>
                            )) : 'Không trả lời'
                          : detail.options && detail.options.length > 0 
                            ? (detail.options[detail.studentAnswer] || 'Không trả lời') 
                            : 'Không trả lời'
                      }</p>
                      <p className={`answer-status ${detail.isCorrect ? 'correct' : 'incorrect'}`}>
                        {detail.isCorrect ? ' ✓ Chính xác' : ' ✗ Chưa chính xác'}
                      </p>
                    </div>
                    
                    {!detail.isCorrect && detail.statements && detail.statements.length > 0 && (
                      <div className="answer-section">
                        <p><strong>Đáp án đúng:</strong></p>
                        {detail.statements.map((stmt, idx) => (
                          <div key={idx} style={{ marginTop: '0.5rem', padding: '0.5rem', background: '#d1fae5', borderRadius: '4px' }}>
                            <span>{stmt.content_vn || stmt.content_en || 'Statement'}</span>
                            <strong style={{ marginLeft: '0.5rem' }}>
                              {stmt.is_true ? '✓ Đúng' : '✗ Sai'}
                            </strong>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {!detail.isCorrect && detail.options && detail.options.length > 0 && (
                      <div className="answer-section">
                        <p><strong>Đáp án đúng:</strong> {detail.options[detail.correctAnswer] || 'Không có'}</p>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.section>
        )}

        {/* Action Buttons */}
        <div className="results-actions">
          <button className="btn-dashboard">
            <Home size={18} />
            Back to Dashboard
          </button>
          <button className="btn-next-quiz">
            <ChevronRight size={18} />
            Take Another Quiz
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Helper: Render cognitive level analysis cards
 */
function renderCognitiveAnalysis(analysis) {
  if (!analysis || !analysis.levels) return null;

  return analysis.levels.map((level, idx) => (
    <motion.div
      key={`level-${level.name}-${idx}`}
      className="analysis-card"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: idx * 0.1 }}
    >
      <h3>{level.name}</h3>
      <div className="score-display">
        <span className="score">{Math.round(level.score)}%</span>
      </div>
      <div className="mini-bar">
        <div className="mini-fill" style={{ width: `${level.score}%` }}></div>
      </div>
      <p className="level-status">{level.status}</p>
      {level.questionCount && (
        <p className="detail">
          {level.correct}/{level.questionCount} correct
        </p>
      )}
    </motion.div>
  ));
}

/**
 * Helper: Get cognitive level display
 */
function getCognitiveLevelDisplay(level) {
  const labels = {
    1: { name: 'Knowledge', icon: '📚', color: '#3b82f6' },
    2: { name: 'Comprehension', icon: '💡', color: '#f59e0b' },
    3: { name: 'Application', icon: '🔧', color: '#8b5cf6' },
    4: { name: 'Analysis', icon: '🧠', color: '#ef4444' }
  };

  const info = labels[level] || labels[1];
  return (
    <span className="cognitive-badge" style={{ '--badge-color': info.color }}>
      {info.icon} Level {level}: {info.name}
    </span>
  );
}

/**
 * Helper: Get difficulty label
 */
function getDifficultyLabel(level) {
  const labels = {
    1: 'Basic (Knowledge)',
    2: 'Intermediate (Understanding)',
    3: 'Advanced (Application)',
    4: 'Expert (Analysis)'
  };
  return labels[level] || 'Unknown';
}
